import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import styled from "styled-components"
import { useForm, useFieldArray, useWatch } from "react-hook-form"
import { z } from 'zod'
import { zodResolver } from "@hookform/resolvers/zod"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../../../ui/form"
import { Input, Label } from "../../../ui/ot"
import { Row, Col } from '../../../ui/RowCol'
import { useLibrary } from '../../controllers/LibraryController'
import MultipleSelector from '../../../ui/multiple-selector';
import { useTranslation } from "react-i18next";

const AuthorisedValuesForm = forwardRef(({
    keyPrefix = 'library.backstage.authorized_values_management',
    onFormFinish = () => { },
}, ref) => {
    const { t } = useTranslation('translation', { keyPrefix })
    const thisJS = "AuthorisedValuesForm"
    const formRef = useRef()

    //#region class schema
    const formSchema = z.object({
        "id": z.number(),
        "category": z.string(),
        "authorised_value": z.string(),
        "lib": z.string().nullish(),
        "lib_opac": z.string().nullish(),
        "imageurl": z.string().nullish(),
        "branches": z.array(z.object({
            label: z.string(),
            value: z.string()
        })).optional().nullish(),
    });
    //#endregion
    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
        },
    })

    const onSubmit = (values) => {
        let branches = values?.branches.map((val) => {
            return { branchcode: val?.value, branchname: val?.label }
        })
        values = {
            ...values,
            branches
        }
        console.log(thisJS, "onSubmit", values)
        onFormFinish(values)
    }
    const onError = (e) => {
        console.log(thisJS, "onError", e)
    }

    useImperativeHandle(ref, () => {
        return {
            onFormSubmit: () => {
                formRef.current.requestSubmit();
            },
            setValue: (name, value, config) => {
                form.setValue(name, value, config)
            },
            setValues: (values) => {
                console.log(thisJS, "setValues", values)
                if (typeof values === 'object') {
                    Object.keys(values).forEach((element) => {
                        form.setValue(element, values[element])
                    })
                }
            },
            getValues: (name) => {
                return form.getValues(name)
            },
        };
    }, []);

    const {
        LibraryData,
        getLibraryData
    } = useLibrary({
        onBefore: () => { },
        onFinish: ({ axiosMode, status, values }) => { }
    })
    useEffect(() => {
        getLibraryData({})
    }, [])

    return (
        <Form {...form}>
            <form ref={formRef} autoComplete="off" onSubmit={form.handleSubmit(onSubmit, onError)}>
                { /* Category => category */}
                <Row className='mb-2' gutter={[16, 16]} align={'middle'} >
                    <Col span={24}>
                        <FormField
                            control={form.control}
                            name="category"
                            render={({ field }) => (
                                <Row gutter={[8, 8]} align={'middle'} >
                                    <Col>
                                        <FieldTitle>{t('category')}:</FieldTitle>
                                    </Col>
                                    <Col>
                                        <FormItem>
                                            <FormControl>
                                                <Label>{field.value ?? ""}</Label>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    </Col>
                                </Row>
                            )}
                        />
                    </Col>
                </Row>
                { /* Authorized value => authorised_value */}
                <Row className='mb-2' gutter={[16, 16]} align={'middle'} >
                    <Col span={24}>
                        <FormField
                            control={form.control}
                            name="authorised_value"
                            render={({ field }) => (
                                <Row gutter={[8, 8]} align={'middle'} >
                                    <Col>
                                        <FieldTitleRed>{t('authorized_values')}:</FieldTitleRed>
                                    </Col>
                                    <Col>
                                        <FormItem>
                                            <FormControl>
                                                <Input placeholder="請輸入..." {...field} value={field.value ?? ""} />
                                            </FormControl>
                                        </FormItem>
                                    </Col>
                                </Row>
                            )}
                        />
                    </Col>
                </Row>
                { /* Description => lib */}
                <Row className='mb-2' gutter={[16, 16]} align={'middle'} >
                    <Col span={24}>
                        <FormField
                            control={form.control}
                            name="lib"
                            render={({ field }) => (
                                <Row gutter={[8, 8]} align={'middle'} >
                                    <Col>
                                        <FieldTitle>{t('description')}:</FieldTitle>
                                    </Col>
                                    <Col xxl={19} xl={19} lg={17} md={15} sm={24} xs={24}>
                                        <FormItem>
                                            <FormControl>
                                                <Input placeholder="請輸入..." {...field} value={field.value ?? ""} />
                                            </FormControl>
                                        </FormItem>
                                    </Col>
                                </Row>
                            )}
                        />
                    </Col>
                </Row>
                { /* Description(OPAC) => lib_opac */}
                <Row className='mb-2' gutter={[16, 16]} align={'middle'} >
                    <Col span={24}>
                        <FormField
                            control={form.control}
                            name="lib_opac"
                            render={({ field }) => (
                                <Row gutter={[8, 8]} align={'middle'} >
                                    <Col>
                                        <FieldTitle>{t('description_opac')}:</FieldTitle>
                                    </Col>
                                    <Col xxl={19} xl={19} lg={17} md={15} sm={24} xs={24}>
                                        <FormItem>
                                            <FormControl>
                                                <Input placeholder="請輸入..." {...field} value={field.value ?? ""} />
                                            </FormControl>
                                        </FormItem>
                                    </Col>
                                </Row>
                            )}
                        />
                    </Col>
                </Row>
                { /* Library limitations => branches */}
                <Row className='mb-2' gutter={[16, 16]} align={'middle'} >
                    <Col span={24}>
                        <FormField
                            control={form.control}
                            name="branches"
                            render={({ field }) => (
                                <Row gutter={[8, 8]} align={'middle'} >
                                    <Col>
                                        <FieldTitle>{t('library_limitations')}:</FieldTitle>
                                    </Col>
                                    <Col xxl={19} xl={19} lg={17} md={15} sm={24} xs={24}>
                                        <FormItem>
                                            <MultipleSelector
                                                value={field.value}
                                                style={
                                                    {
                                                        minWidth: "200px"
                                                    }
                                                }
                                                onChange={(e) => {
                                                    field.onChange(e)
                                                }}
                                                selectFirstItem={false}
                                                options={LibraryData.map((value) => {
                                                    return {
                                                        label: value?.branchname,
                                                        value: value?.branchcode,
                                                    }
                                                })}
                                                placeholder="請輸入"
                                                creatable={true}
                                                emptyIndicator={
                                                    <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
                                                        沒有選項
                                                    </p>
                                                }
                                            />
                                            <FormMessage />
                                        </FormItem>
                                    </Col>
                                </Row>
                            )}
                        />
                    </Col>
                </Row>
            </form>
        </Form>
    )
})
const FieldTitle = styled.div`
text-align: left;
font-size: 1.1em;
font-weight: 500;
`
const FieldTitleRed = styled.div`
text-align: left;
font-size: 1.1em;
font-weight: 500;
color: red;
`

export default AuthorisedValuesForm